import { DOCUMENT_CATEGORIES, type DocumentCategory } from './domain';

/**
 * Canonical document naming.
 *
 * A file arrives called `20974588317 (3)(1).pdf`, `IMG_2931.jpg` or
 * `PHOTO-2026-09-15-19-26-52.jpg`. None of those tell anyone what the document
 * IS, and in a list of two hundred attachments that is the difference between
 * a usable archive and a shoebox.
 *
 * So a document gets a derived, meaningful name built from what it is attached
 * to: category, the entity's own reference, and a date. The name the user
 * uploaded is never thrown away — it is kept in `original_file_name`, because
 * it is provenance: it is how the file was referred to in the email or the
 * folder it came from.
 *
 * THREE THINGS THIS DOES NOT CHANGE:
 *
 *  1. The R2 key stays `sha256/<hash>`. Content addressing is what makes blobs
 *     immutable, deduplicated, and safe to never delete — which is what makes
 *     D1 Time Travel a complete restore. Renaming is metadata only; the bytes
 *     never move.
 *  2. Identifiers keep their exact value in the database. A contract number
 *     `20974588317/1-0` cannot contain a slash in a FILENAME, so the slug uses
 *     a dash — but the lease row still holds the exact original.
 *  3. Nothing is overwritten. Two documents that derive the same name are
 *     disambiguated by a short content-hash suffix.
 */

/** Arabic category labels, used as the leading segment of a filename. */
const CATEGORY_SLUG: Record<DocumentCategory, string> = {
  lease: 'عقد',
  ejar_certificate: 'شهادة-إيجار',
  title_deed: 'صك',
  invoice: 'فاتورة',
  receipt: 'سند-قبض',
  bank_statement: 'كشف-بنكي',
  legal_filing: 'مذكرة',
  legal_report: 'تقرير-قانوني',
  valuation: 'تقييم',
  survey: 'مساحة',
  correspondence: 'مراسلة',
  photo: 'صورة',
  proposal: 'عرض',
  quotation: 'عرض-سعر',
  insurance: 'تأمين',
  municipal: 'بلدية',
  other: 'مستند',
};

/**
 * Characters that must not appear in a filename.
 *
 * Covers the union of what Windows, macOS and Linux reject or treat specially,
 * plus control characters. Arabic letters, digits, dashes and underscores
 * survive — the name is meant to be read by Arabic speakers.
 */
// eslint-disable-next-line no-control-regex
const UNSAFE = /[\x00-\x1F\x7F<>:"/\\|?*\u200B-\u200F\u202A-\u202E]/g;

/** Trailing dots and spaces break on Windows; leading dots hide the file. */
function trimUnsafeEdges(value: string): string {
  return value.replace(/^[.\s]+/, '').replace(/[.\s]+$/, '');
}

/**
 * Turn arbitrary text into a filename-safe slug, preserving Arabic.
 * Whitespace and unsafe characters collapse to single dashes.
 */
export function slugifyForFileName(value: string | null | undefined, maxLength = 60): string {
  if (!value) return '';
  const cleaned = trimUnsafeEdges(
    value
      .normalize('NFKC')
      .replace(UNSAFE, '-')
      .replace(/[\s_]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, ''),
  );
  return cleaned.slice(0, maxLength).replace(/-+$/, '');
}

/** Windows reserves these names regardless of extension. */
const RESERVED = new Set([
  'con',
  'prn',
  'aux',
  'nul',
  ...Array.from({ length: 9 }, (_, i) => `com${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `lpt${i + 1}`),
]);

/** Extract a lowercase extension from an uploaded filename. */
export function extensionOf(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? '';
  const idx = base.lastIndexOf('.');
  if (idx <= 0 || idx === base.length - 1) return '';
  return base
    .slice(idx + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 8);
}

export interface DocumentNameContext {
  category: DocumentCategory | string;
  /**
   * The reference of whatever the document is attached to: a contract number,
   * a property name, a tenant name, a unit number. Slugified, never trusted.
   */
  entityRef?: string | null;
  /** A secondary qualifier, e.g. the tenant on a lease document. */
  qualifier?: string | null;
  /** Document date — the lease start, the invoice date, the report date. */
  date?: string | null;
  /** The name the user uploaded, used only for its extension. */
  originalFileName: string;
  /** Content hash; its first six characters disambiguate collisions. */
  sha256?: string | null;
}

/**
 * Build the canonical filename.
 *
 * Shape: `<category>-<entityRef>-<qualifier>-<date>-<hash6>.<ext>`
 * with every empty segment omitted.
 *
 *   عقد-20974588317-1-0-بيت-الشطيرة-2026-11-15-a1b2c3.pdf
 *   تقرير-قانوني-2026-08-30-9f4e21.pdf
 *   صورة-الخزامى-2026-09-15-77ab0c.jpg
 *
 * The six-character hash suffix is not decoration: two photos of the same unit
 * taken the same day would otherwise derive identical names, and a document
 * list where three rows read the same is no better than `IMG_2931.jpg`.
 */
export function buildDocumentFileName(ctx: DocumentNameContext): string {
  const category = (DOCUMENT_CATEGORIES as readonly string[]).includes(ctx.category)
    ? CATEGORY_SLUG[ctx.category as DocumentCategory]
    : CATEGORY_SLUG.other;

  /**
   * When the document is not attached to anything, fall back to the uploaded
   * name's stem rather than emitting a bare `مستند-2026-09-15-a1b2c3.pdf`.
   *
   * A name that carries no entity reference AND discards the original is
   * strictly worse than the original — `تقرير المحامي 30 أغسطس 2026.pdf`
   * becoming `مستند-…` loses everything a person could search for. The
   * fallback is skipped for names that are themselves uninformative
   * (`IMG_2931`, `PHOTO-2026-…`, a bare number), which are exactly the ones
   * worth replacing.
   */
  const explicitRef = slugifyForFileName(ctx.entityRef, 48);
  const fallbackRef =
    explicitRef || isUninformativeFileName(ctx.originalFileName)
      ? ''
      : slugifyForFileName(ctx.originalFileName.replace(/\.[^.]+$/, ''), 48);

  const segments = [
    category,
    explicitRef || fallbackRef,
    slugifyForFileName(ctx.qualifier, 40),
    // Dates are already safe and sortable; keep them verbatim.
    ctx.date && /^\d{4}-\d{2}-\d{2}$/.test(ctx.date) ? ctx.date : '',
    ctx.sha256 ? ctx.sha256.slice(0, 6) : '',
  ].filter(Boolean);

  let stem = segments
    .join('-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  // Guard the pathological cases: an empty stem, or a Windows device name.
  if (!stem) stem = CATEGORY_SLUG.other;
  if (RESERVED.has(stem.toLowerCase())) stem = `${stem}-file`;

  // Leave headroom for the extension inside the 200-character column limit.
  stem = stem.slice(0, 180).replace(/-+$/, '');

  const ext = extensionOf(ctx.originalFileName);
  return ext ? `${stem}.${ext}` : stem;
}

/**
 * True when a name carries no information — the cases worth replacing.
 * Used to decide whether to show the original alongside the canonical name.
 */
export function isUninformativeFileName(fileName: string): boolean {
  const stem = fileName.replace(/\.[^.]+$/, '').trim();
  return (
    stem.length === 0 ||
    // `IMG_2931`, `PHOTO-2026-09-15-19-26-52`, `Scan 001` — a camera or
    // scanner prefix followed by nothing but digits and separators.
    /^(img|image|photo|dsc|scan|doc|document|file|untitled|whatsapp)[-_ ]?[\d\s._-]*$/i.test(
      stem,
    ) ||
    /^\d[\d\s()._-]*$/.test(stem)
  );
}
